<template>
	<modal @close="$emit('close')" :clickBackdropClosing="false" width="98%" :showModalCloseButton="false" :noTitleBar="true" :forceHeight="forceHeight" :maxWidth="maxWidth">
		<div slot="footer">
			<div class="d-flex align-items-center px-2 py-2">
				<div class="flex-grow-0" v-if="isClipboardSupported">
					<button class="btn btn-sm btn-outline-secondary" @click="copyShareLink">
						{{$t('general.share')}}
					</button>
				</div>
				<div style="flex-grow:1;"></div>
				<div class="text-right">
					<button cy-button="add-items" class="btn btn-sm btn-primary ml-3" style="word-break:keep-all;"
						@click="confirm">{{$t('general.confirm')}}</button>
				</div>
			</div>
		</div>
		<div>
            <div class="popup-gray-close-button absolute" @click="$emit('close')">
                <img src="/img/close_cross@2x.png" />
            </div>
			<div v-if="imageItems.length > 1" :class="thumbnailContainerClasses" class="relative thumbnail-outer-container">
				<div ref="thumbnail-container" class="thumbnail-container white-space-wrap hide-native-scrollbar">
					<div v-for="(imageItem, i) in imageItems" :key="imageItem.key" class="thumbnail-item" @click="$refs['swiper'].swiperInstance.slideTo(i)" ref="thumbnail-items">
						<ding-image :src="imageItem.imageUrl" aspect-ratio="1:1" mode="fill" />
					</div>
				</div>
				<div v-if="currentIndex > 0" class="thumbnail-arrow thumbnail-up-arrow" @click="goToPrev">
					<img src="/img/icon_arrow_up.png" />
				</div>
				<div v-if="currentIndex < imageItems.length - 1" class="thumbnail-arrow thumbnail-down-arrow" @click="goToNext">
					<img src="/img/icon_arrow_down.png" />
				</div>
			</div>
			<div class="relative image-swiper-container">
				<div v-if="canGoPrev && !hideSwiperButton" class="swiper-button swiper-prev-button">
					<div class="inner" @click="goToPrev">
						<img class="forward-icon" src="/img/backward.png" />
					</div>
				</div>
				<swiper ref="swiper" :slides-per-view="3" :space-between="0" @slideChangeTransitionEnd="onSlideChange">
					<swiper-slide v-for="imageItem in imageItems" :key="imageItem.key">
						<ding-image :src="imageItem.imageUrl" width="100%" aspect-ratio="1:1" mode="fill" :zoomOnHover="true" @click="selectItem(imageItem)"/>
					</swiper-slide>
				</swiper>
				<div v-if="canGoNext &&!hideSwiperButton" class="swiper-button swiper-next-button" @click="$refs['swiper'].swiperInstance.slideNext(300)">
					<div class="inner">
						<img class="forward-icon" src="/img/forward.png" />
					</div>
				</div>
			</div>
		</div>
		<div style="clear:both;"></div>
		<div class="px-3">
			<div class="d-flex align-items-center flex-wrap">
				<div class="flex-grow-1">
					<h4 class="item-title">{{item.name}}</h4>
					<div v-if="showRemainStock" class="mb-2 remaining-stock">
						<span class="mr-1">{{$t('remaining_label')}}</span><span>{{showRemainStock}}</span>
					</div>
					<div class="d-inline-block" v-if="noSpecs">
						<buyable-price :provider="provider" :item="item"/>
					</div>
					<div v-if="isTimeLimit" class="pt-1 time-limit-content">
						<div v-if="availablePeriods.length > 0">
							<div v-for="(period, id) in availablePeriods" :key="id">
								{{$t('time_limit_label')}} {{availablePeriodsToDisplay(period)}}
							</div>
						</div>
						<div v-if="availableWeekdays.length > 0" class="d-flex">
							<div class="mr-1">{{$t('time_limit_label')}}</div>
							<div v-for="(weekday, id) in availableWeekdays" :key="weekday">
								<span>{{$t('message.weekday_' + weekday)}}</span><span v-if="(id + 1) < availableWeekdays.length">{{$t('common')}}</span>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-grow-0 flex-shrink-0">
					<div class="d-flex align-items-center" v-if="noSpecs">
						<span v-if="hasItemSoldOut">{{$t('sold_out')}}</span>
						<span v-else-if="notAvailableOnDate">{{$t('not_available_on_day')}}</span>
						<span v-else-if="hasReachedGroupLimit">{{$t('reached_group_limit')}}</span>
						<item-count-control v-else :item="item" @count-changed="onCountChange" :group-amount-left="groupAmountLeft()" />
					</div>
				</div>
			</div>
			<div class="pt-2 pb-2 item-description" v-html="item.description.nl2brIfNotHTML()"></div>

			<!-- Multiple Specs UI -->
			<div v-if="item.support_multiple_specs">
				<div><small class="text-muted">{{ $t('please_select_spec_attribute') }}</small></div>
				<div class="d-flex d-flex-wrap mb-4" style="gap: 16px;">
					<!-- Show all attributes -->
					<div class="" v-for="attribute in item.spec_attributes" :key="attribute.id">
						<select v-if="attribute.values" v-model="selectedSpecAttributeMap[attribute.name]" class="form-control form-control-inline">
							<option :value="null" disabled selected>{{ attribute.name }}</option>
							<option v-for="value in attribute.values.split('||')" :value="value">
								{{ value }}
							</option>
						</select>
					</div>
				</div>
				<div v-if="multipleSpecTargetSpec" ref="multipleSpecTargetContainer" class="pb-2">
					<div><small class="text-muted">{{ $t('general.count') }}</small></div>
					<item-count-control
						class="mb-4"
						:item="item"
						:spec="multipleSpecTargetSpec"
						@count-changed="onCountChange" :group-amount-left="groupAmountLeftForSpec(multipleSpecTargetSpec)"
					/>
				</div>
			</div>
			<!-- Normal Specs UI -->
			<div v-else-if="item.specs.length > 0">
				<div v-for="spec in item.specs" :key="spec.id" class="mb-2">
					<div class="spec d-block d-sm-flex align-items-center" @click="focusToSpecImageIfPossible(spec)">
						<div class="mr-4">
							<b>{{spec.name}}</b>
							<div v-if="spec.fixed_date_time">
								<small>{{ $t('time_limit_label') }} {{ formatDatetime(spec.fixed_date_time) }}</small>
							</div>
							<div v-if="showSpecRemainStock(spec)" class="remaining-stock">
								<span class="mr-1">{{$t('remaining_label')}}</span><span>{{showSpecRemainStock(spec)}}</span>
							</div>
							<div class="d-inline-block d-sm-block"> <buyable-price :provider="provider" :item="item" :spec="spec" /> </div>
						</div>
						<div class="flex-grow-1"></div>
						<div class="flex-grow-0 flex-shrink-0">
							<span v-if="hasSpecSoldOut(spec)">{{$t('sold_out')}}</span>
							<span v-else-if="hasSpecReachedGroupLimit(spec)">{{$t('reached_group_limit')}}</span>
							<span v-else-if="notAvailableOnDate || notAvailableDueToSpecFixedDate(spec)">{{$t('not_available_on_day')}}</span>
							<span v-else-if="notAvailableDueToIncludedInBookingPass(spec)">{{$t('included_in_booking_pass')}}</span>
							<item-count-control v-else :item="item" :spec="spec" @count-changed="onCountChange" :group-amount-left="groupAmountLeftForSpec(spec)" />
						</div>
					</div>
				</div>
			</div>
			<photo-viewer
				v-if="selectedPhoto"
				ref="photoViewer"
				:photos="photos"
				:context="context"
				@close="selectedPhoto = null"></photo-viewer>
		</div>
	</modal>
</template>

<script>
import moment from 'moment';
import RootStore from '../stores/root';
import DingImage from '../components/common/Image';
import { mapActions, mapGetters } from 'vuex';
import prompt from '../mixins/prompt-confirm';
import BuyablePrice from './items/BuyablePrice';
import { EventAddToCart } from '../bu/models/GA4';
import PhotoViewer from './common/PhotoViewer.vue';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';

export default {
	store: RootStore,
	mixins: [prompt],
	components: {DingImage, BuyablePrice, PhotoViewer, Swiper, SwiperSlide },
	props: {
		item: { required: true, type: Object },
		context: { required: true, type: Object},
		provider: { required: true, type: Object },
		questionId: { reuqired: true, type: Number },
		stockInfoMap: { required: true, type: Object },
		itemGroupId: { type: Number }
	},
	computed: {
		...mapGetters({
			'allCartItems': 'cart/items',
			groupAmountLeftInfo: 'cart/groupAmountLeftInfo',
			bookingPassItem: 'cart/bookingPassItem'
		}),
		multipleSpecTargetSpec() {
			return this.item.specs.find((spec) => {
				if (spec.spec_attribute_map) {
					const map = JSON.parse(spec.spec_attribute_map);
					for(var k in this.selectedSpecAttributeMap) {
						if (!this.selectedSpecAttributeMap[k] || map[k] != this.selectedSpecAttributeMap[k]) {
							return false;
						}
					}
				}
				return true;
			});
		},
		photos() {
			return this.imageItems.map((item) => {
				return {
					path: item.imageUrl,
					text: ''
				}
			});
		},
		cartItems() {
			return this.allCartItems.filter((item) => item.itemId || item.specId);
		},
		canGoNext() {
			return this.currentIndex < this.imageItems.length - 1;
		},
		canGoPrev() {
			return this.currentIndex > 0;
		},
		noSpecs() {
			return this.item.specs.length == 0
		},
		hasItemSoldOut() {
			if (typeof this.stockInfoMap['items'][this.item.id] === 'undefined' || this.stockInfoMap['items'][this.item.id] === null) {
				return false;
			}
			return this.stockInfoMap['items'][this.item.id] <= 0;
		},
		hasReachedGroupLimit() {
			if (this.item.specs && this.item.specs.length > 0) {
				return false;
			}
			for(var i in this.groupAmountLeftInfo) {
				const group = this.groupAmountLeftInfo[i];
				if (group.item_ids.indexOf(this.item.id) >= 0 && group.amount_lefts == 0) {
					return true;
				}
			}
			return false;
		},
		imageItems() {
			var imageItems = [];
			if (this.item.images) {
				for(var i in this.item.images) {
					const image = this.item.images[i];
					imageItems.push({
						key: `item-${image.id}`,
						imageUrl: this.context.storageEndpoint + image.file_name
					});
				}
			}
			for(var i in this.item.specs) {
				const spec = this.item.specs[i];
				if (spec.image_path) {
					imageItems.push({
						key: `spec-${spec.id}`,
						imageUrl: this.context.storageEndpoint + spec.image_path
					});
				}
			}
			return imageItems;
		},
		notAvailableOnDate() {
			if (this.context.selectedDate) {
				return this.notAvailableDueToPeriod || this.notAvailableDueToWeekday;
			} else {
				return false;
			}
		},
		notAvailableDueToPeriod() {
			const item = this.item;
			if (!this.context.selectedDate) {
				return false;
			}
			if (item.availablePeriods.length == 0) {
				return false;
			}
			const now = moment(this.context.selectedDate.replaceAll('/', '-')).tz(this.provider.timezone);
			const okPeriod = item.availablePeriods.find((p) => {
				const start = moment.tz(p.start_datetime, 'GMT').tz(this.provider.timezone);
				const end = moment.tz(p.end_datetime, 'GMT').tz(this.provider.timezone);
				return now.isBetween(start, end, null, '[]');
			});
			return !okPeriod;
		},
		notAvailableDueToWeekday() {
			const item = this.item;
			if (!this.context.selectedDate) {
				return false;
			}
			if (item.availableWeekdays.length == 0) {
				return false;
			}
			const weekday = moment(this.context.selectedDate.replaceAll('/', '-')).isoWeekday();
			const okWeekday = item.availableWeekdays.find((item) => {
				return item == weekday;
			})
			return !okWeekday;
		},
		isTimeLimit () {
			return (this.item.availablePeriods.length > 0 || this.item.availableWeekdays.length > 0); 
		},
		availableWeekdays () {
			return this.item.availableWeekdays;
		},
		availablePeriods () {
			return this.item.availablePeriods;
		},
		showRemainStock () {
			if (this.item.show_stock_info && this.noSpecs) {
				return this.stockInfoMap['items'][this.item.id];
			}
			return false;
		},
		isClipboardSupported() {
			return navigator.clipboard && window.isSecureContext;
		},
	},
	data() {
		const isMobile = window.innerWidth <= 480;
		const isPad = window.innerWidth > 480 && window.innerWidth <= 1024;
		var specCountMap = {};
		for(var i in this.item.specs) {
			specCountMap[this.item.specs[i].id] = 0;
		}
		let maxWidth = 700;
		if (isMobile) {
			maxWidth = 700;
		} else {
			if (isPad) {
				maxWidth = 560;
			} else {
				maxWidth = 640;
			}
			if (this.item.images.length === 1) {
				maxWidth -= 80;
			}
		}

		let selectedSpecAttributeMap = {};
		for(var k in this.item.spec_attributes) {
			const attribute = this.item.spec_attributes[k];
			if (attribute.values) {
				selectedSpecAttributeMap[attribute.name] = null;
			}
		}
		return {
			hideSwiperButton: false,
			selectedPhoto: null,
			count: 0,
			specCountMap,
			currentIndex: 0,
			thumbnailContainerClasses: {
				'has-content-upward': false,
				'has-content-downward': false
			},
			isMobile,
			maxWidth: maxWidth + 'px',
			forceHeight: isMobile ? '84vh' : '96vh',
			selectedSpecAttributeMap
		};
	},
	mounted() {
		// Add item_id to query string
		const url = new URL(window.location.href);
		url.searchParams.set('item_id', this.item.id);
		if (this.itemGroupId) {
			url.searchParams.set('item_group_id', this.itemGroupId);
		}
		window.history.pushState({}, '', url);

		this.$nextTick(() => {
			const hasThumbnailWindow = (!this.isMobile && this.imageItems.length > 1);
			const css = {
				width: hasThumbnailWindow ? '80%' : '100%'
			};
			if (hasThumbnailWindow) {
				$('.thumbnail-container, .thumbnail-outer-container').css({height: `${$('.image-swiper-container').height()}px`});
				this.$refs['thumbnail-container'].addEventListener('scroll', this.updateThumbnailContainerClasses);
				this.hideSwiperButton = true;
			} else {
				$('.thumbnail-container, .thumbnail-outer-container').remove();
				css.width = '100%';
				$('.image-swiper-container').css(css);
			}
			this.$refs.swiper.swiperInstance.update();
		});

		this.descriptionImages = this.item.images.filter(image => image.type === 'ITEM_DESCRIPTION')
			.sort((a, b) => a.order - b.order);

		this.descriptionImages.forEach(image => {
			const replaceString = `id="img-${image.id}" src="${this.context.storageEndpoint + image.file_name}"`
			this.item.description = this.item.description.replace(/src=""/, replaceString);
		})
	},
	beforeDestroy() {
		// Remove item_id from query string
		const url = new URL(window.location.href);
		url.searchParams.delete('item_id');
		url.searchParams.delete('item_group_id');
		window.history.pushState({}, '', url);

		if (this.zoomOnHover) {
			this.$refs['thumbnail-container'].removeEventListener('scroll', this.updateThumbnailContainerClasses);
		}
	},
	watch: {
		multipleSpecTargetSpec(value) {
			if (value) {
				this.$nextTick(() => {
					this.$refs['multipleSpecTargetContainer']?.scrollIntoView({
						behavior: "smooth",
						block: "end",
						inline: "nearest",
					});
				})
			}
		},
		currentIndex() {
			if (this.currentIndex) {
				this.$refs['thumbnail-items'][this.currentIndex].scrollIntoView(false);
			}
		}
	},
	methods: {
		...mapActions({
			setItem: 'cart/setItem'
		}),
		notAvailableDueToIncludedInBookingPass(spec) {
			const bookingPass = this.bookingPassItem?.bookingPass;
			const preset = bookingPass?.presets.length > 0 ? bookingPass.presets[0] : null;
			if (!preset || !preset.provider_item_spec_id) {
				return false;
			}
			return spec.id === preset.provider_item_spec_id;
		},
		goToNext() {
			this.$refs['swiper'].swiperInstance.slideNext(300);
		},
		goToPrev() {
			this.$refs['swiper'].swiperInstance.slidePrev(300);
		},
		updateThumbnailContainerClasses() {
			const $el = this.$refs['thumbnail-container'];
			this.thumbnailContainerClasses = {
				'has-content-upward': $el.scrollTop > 0,
				'has-content-downward': $el.scrollTop + $el.clientHeight < $el.scrollHeight
			}
		},
		selectItem(imageItem) {
			this.selectedPhoto = imageItem;
			this.$nextTick(() => {
				this.$refs['photoViewer'].selectPhoto(this.photos[this.imageItems.indexOf(imageItem)]);
			});
		},
		groupAmountLeft() {
			var memo = Number.MAX_VALUE;
			for(var i in this.groupAmountLeftInfo) {
				const group = this.groupAmountLeftInfo[i];
				if (group.item_ids.indexOf(this.item.id) >= 0) {
					const amountLeftConsideredCartItems = Math.max(0, group.amount_lefts - this.count);
					if(memo >= amountLeftConsideredCartItems) {
						memo = amountLeftConsideredCartItems;
					}
				}
			} 
			return memo === Number.MAX_VALUE ? null : memo;
		},
		groupAmountLeftForSpec(spec) {
			var memo = Number.MAX_VALUE;
			for(var i in this.groupAmountLeftInfo) {
				const group = this.groupAmountLeftInfo[i];
				var relatedCartItemCount = 0;

				if (group.spec_ids.indexOf(spec.id) >= 0) {
					for(var k in group.spec_ids) {
						const specId = group.spec_ids[k];
						if (this.item.specs.find((s) => s.id === specId)) {
							if (this.specCountMap[specId]) {
								relatedCartItemCount += this.specCountMap[specId];
							}
						} else {
							const cartItem = this.cartItems.find((cartItem) => cartItem.specId === specId);
							if (cartItem) {
								relatedCartItemCount += cartItem.count;
							}
						}
					}
					const amountLeftConsideredCartItems = Math.max(0, group.amount_lefts - relatedCartItemCount);
					if(memo >= amountLeftConsideredCartItems) {
						memo = amountLeftConsideredCartItems;
					}
				}
			} 
			return memo === Number.MAX_VALUE ? null : memo;
		},
		hasSpecReachedGroupLimit(spec) {
			for(var i in this.groupAmountLeftInfo) {
				const group = this.groupAmountLeftInfo[i];
				if (group.spec_ids.indexOf(spec.id) >= 0 && group.amount_lefts == 0) {
					return true;
				}
			}
			return false;
		},
		hasSpecSoldOut(spec) {
			if (typeof this.stockInfoMap['specs'][spec.id] === 'undefined' || this.stockInfoMap['specs'][spec.id] === null) {
				return false;
			}
			return this.stockInfoMap['specs'][spec.id] <= 0;
		},
		onSlideChange() {
			this.currentIndex = this.$refs['swiper'].swiperInstance.activeIndex;
		},
		onCountChange({item, spec, count}) {
			if (spec) {
				this.specCountMap[spec.id] = count;
			} else {
				this.count = count;
			}
			this.$forceUpdate();
		},
		confirm() {
			if (this.noSpecs) {
				this.setItem({item: this.item, spec: null, count: this.count, questionId: this.questionId})
			} else {
				for(var specId in this.specCountMap) {
					const spec = this.item.specs.find((spec) => spec.id == specId);
					const count = this.specCountMap[specId]
					// if it's normal(not multiple specs) the counts should be set no matter what
					// if it's multiple specs item, only set the specs with count > 0
					const shouldSetSpecInCart = !this.item.support_multiple_specs || count > 0;
					if (shouldSetSpecInCart) {
						this.setItem({
							item: this.item,
							spec,
							count,
							questionId: this.questionId
						})
						if (count) {
							this.context.bus.$emit('add-to-cart', this.item, spec);
						}
					}
				}
			}
			this.trackSetItem();
			this.$emit('close');
		},
		focusToSpecImageIfPossible(spec) {
			const index = this.imageItems.findIndex((item) => item.key === `spec-${spec.id}`);
			if (index >= 0) {
				this.$refs.swiper.swiperInstance.slideTo(index, 300);
			}
		},
		showSpecRemainStock(spec){
			if (!this.item.show_stock_info) {
				return false;
			}
			return this.stockInfoMap['specs'][spec.id];
		},
		availablePeriodsToDisplay (period) {
			const start = moment(period.start_datetime).tz(this.provider.timezone);
			const end = moment(period.end_datetime).tz(this.provider.timezone);

			const formattedStart = start.format('Y/M/D');
			const isSameYear = start.format('Y') == end.format('Y');
			const formattedEnd = isSameYear ? end.format('M/D') : end.format('Y/M/D');
			
			const result = formattedStart + this.$t('to') + formattedEnd;
			return result;
		},
		notAvailableDueToSpecFixedDate(spec) {
			const item = this.item;
			if (!this.context.selectedDate || !spec.fixed_date_time) {
				return false;
			}

			const selectedDate = moment(this.context.selectedDate);
			const fixedDateTime = moment.tz(spec.fixed_date_time.replaceAll('-', '/'), 'GMT').tz(this.provider.timezone);

			return !selectedDate.isSame(fixedDateTime);
		},
		formatDatetime(date) {
			return moment.tz(date, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD A h:mm');
		},
		trackSetItem() {
			let value = 0;
			let items = [];
			const item = this.item;
			if (this.noSpecs && this.count > 0) {
				const itemData = Object.assign({}, {
					item_id: item.id,
					item_name: item.name,
					quantity: this.count
				})
				if (item.is_renting) {
					value = item.renting_charge_amount * this.count;
					itemData.price = item.renting_charge_amount;
				} else {
					value = item.price * this.count;
					itemData.price = item.price;
				}
				items.push(itemData);
			} else {
				for(var specId in this.specCountMap) {
					const spec = this.item.specs.find((spec) => spec.id == specId);
					if (this.specCountMap[specId] > 0) {
						const specPrice = item.is_renting ? spec.renting_charge_amount : spec.price;
						value = value + (specPrice * this.specCountMap[specId]);
						items.push({
							item_id: item.id,
							item_name: item.name + '/' + spec.name,
							price: specPrice,
							quantity: this.specCountMap[specId],
							spec_id: spec.id
						})
					}
				}
			}
			if(items.length > 0) {
				window.tracker.track(() => new EventAddToCart(
					this.provider.currency,
					value,
					items
				));
			}
		},
		copyShareLink() {
			const url = window.location.href;
			navigator.clipboard.writeText(url).then(() => {
				this.promptAlert(this.$t('general.item_link_copied'), () => {}, 'success');
			}).catch(() => {
				this.promptAlert(this.$t('general.copy_failed'));
			});
		}
	}
}
</script>

<style scoped lang="scss">

	.item-title {
		font-size: 1.3rem;
		margin: 1rem 0 0.8rem;
		font-weight: bold;
	}

	.item-description {
		font-size: 0.9rem;
		width: 100%;

		/deep/ img {
			max-width: 100%;
		}
		
		/deep/ p {
			margin: 0;
		}
	}

.spec {
	padding: 8px;
	border-radius: 8px;
	border: 1px solid #EEE;

	&:hover {
		background: #F4f4f4;
		cursor: pointer;
	}
}

.flex-grow-1 {
	flex-grow: 1;
}

.swiper-prev-button {
	right: 56px;
}

.swiper-next-button {
	right: 0;
}

.swiper-button {
	padding: 12px;
	z-index: 10;
	position: absolute;
	bottom: 0px;
	cursor: pointer;
	display: flex;
	user-select: none;;

	&:hover {
		opacity: 0.75;
	}

	.inner {
		width: 44px;
		height: 44px;
		border-radius: 22px;
		background-color: #41414199;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 25px;
			height: 25px;
		}
	}
}
	.time-limit-content{
		opacity: 0.6;
		font-size: 12px;
	}
	.remaining-stock{
		font-size: 12px;
		color: #666;
	}

	/deep/ .ding-modal-title-bar {
		background: white;;
	}

	.thumbnail-outer-container {
		position: relative;
		width: 16%;
		display: block;
		float: left;
		overflow: hidden;
	}

	.thumbnail-container {
		overflow: auto;
		padding: 6px 0 36px;
		margin: 0px 6px 36px;
	}

	.thumbnail-item {
		cursor:pointer;
		&:hover {
			opacity: 0.75;
		}
		margin-bottom: 8px;
	}

	.image-swiper-container {
		width: 84%;
		float: left;
	}

	.thumbnail-arrow {
		background: #41414199;
		height: 30px;
		padding: 5px 0;
		width: 100%;
		cursor: pointer;
		&:hover {
			background: #000000aa;
		}
		text-align: center;

		&.thumbnail-up-arrow {
			position: absolute;
			top: 0;
			left: 0;
			img {
				margin-top: 4px;
			}
		}
		&.thumbnail-down-arrow {
			position: absolute;
			bottom: 0;
			left: 0;
			img {
				margin-bottom: 4px;
			}
		}

		img {
			height: 20px;
		}
	}

</style>

<i18n>
{
	"zh": {
		"add_to_cart": "加到購物袋",
		"items_added_to_cart": "已更新購物袋",
		"not_available_on_day": "非可訂日",
		"remaining_label": "剩餘",
		"time_limit_label": "限",
		"common": "、",
		"to": "-",
		"reached_group_limit": "已售完",
		"please_select_spec_attribute": "請選擇規格",
		"included_in_booking_pass": "已於課卡內購買"
	},
	"en": {
		"add_to_cart": "Add To Cart",
		"items_added_to_cart": "Cart Updated",
		"not_available_on_day": "No Available Day",
		"remaining_label": "Remain",
		"time_limit_label": "Only",
		"common": ",\u00A0",
		"to": "-",
		"reached_group_limit": "Sold Out",
		"please_select_spec_attribute": "Please choose spec",
		"included_in_booking_pass": "Included in Booking Pass"
	}
}
</i18n>